import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { NextPage } from '@cvx/nextpage';
import { forkJoin } from 'rxjs';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { HomeApprovalService } from '../../../services/home-approval.service';
import { AdminService } from '../../../services/admin.service';
import { MenuService } from '../../../services/menu.service';
import { CalService } from '../../../services/base/cal.service';
import { OperatorService } from '../../../services/operator.service';
import { NotificationService } from '../../../services/notification.service';


@Component({
    selector: 'app-approval-tab',
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ButtonModule,
        TableModule,
        InputTextModule,
        CardModule,
        ToastModule,
        NextPage
    ],
    templateUrl: './approval-tab.component.html',
    styleUrls: ['./approval-tab.component.scss']
})
export class ApprovalTabComponent implements OnInit {
  tagNumber: any;
  activeTabIndex: number = 0;
  // Data objects for the tables
  operationData: any = {};
  inspectionData: any = {};
  oaData: any = {};
  repumpHistoryData: any[] = [];
  // Approval data
  approvalRecords: any[] = [];
  showSaveButton: boolean = false;
  showReOpenButton: boolean = false; showReSendButton: boolean = false;
  loggedInCAI: any;
  showValidationErrors: boolean = false;

  // Repump Approval data
  repumpApprovalRecords: any[] = [];
  showRepumpSaveButton: boolean = false;
  showRepumpReOpenButton: boolean = false;
  showRepumpReSendButton: boolean = false;
  showRepumpValidationErrors: boolean = false;
  recordUpdated: boolean = false;
  noRecordsTOSave: boolean = false;
  emailSent: boolean = false;
  totalRepumpCount: number = 0;
  responseMessage = '';
  repumpMessage = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private messageService: NotificationService,
    private homeApprovalService: HomeApprovalService,
    private adminService: AdminService,
    private menuService: MenuService,
    private calService: CalService,
    private operatorService: OperatorService
  ) {
    this.route.params.subscribe(params => {
      const tagNumber = params['tagNumber'];
      this.tagNumber = tagNumber;
    });
  }
  ngOnInit(): void {
    this.loggedInCAI = sessionStorage.getItem('currentUser') || '';
    
    // Extract facility ID from URL query parameter 'f' and update localStorage
    this.route.queryParams.subscribe(params => {
      const facilityId = params['f'];
      if (facilityId) {
        localStorage.setItem('current_facility_id', facilityId);
      }
    });

    this.homeApprovalService.getHomeData(this.tagNumber).subscribe({
      next: (response) => {
        // Map Operation data
        if (response.Opeartion && response.Opeartion.length > 0) {
          const operation = response.Opeartion[0];
          this.operationData = {
            dateEntered: this.formatDate(operation.de_date_entered),
            operatorCAI: operation.de_cai,
            repairType: operation.repair_type_name,
            tagNumber: operation.TagNumber,
            division: operation.division_name,
            plant: operation.plant_name,
            section: operation.section_name,
            associatedPlant: operation.assoc_plant_shutdown,
            leakingFrom: operation.leaking_name,
            leakingFromOther: operation.leaking_other,
            operatingPSI: operation.operating_PSI,
            operatingTemp: operation.operating_temperature,
            materialState: operation.material_state_name,
            comments: operation.operator_comments,
            shutdownDescription: operation.shutdown_description,
            permRepairCompleted: operation.PermRepairComplete == '0' ? 'No' : (operation.PermRepairComplete ? 'Yes' : ''),
            rule1173: operation.rule_1173,
            tlrStatus: this.getTlrStatusText(operation.tlr_status),
            services: operation.service_operations,
            serviceOther: operation.service_other,
            techJustification: operation.TechnicalJustification
          };
        }
        // Map Inspections data
        if (response.Inspections && response.Inspections.length > 0) {
          const inspection = response.Inspections[0];
          this.inspectionData = {
            InspectionISONo: inspection.InspectionISONo,
            inspectionDate: this.formatDate(inspection.inspection_date),
            inspectorCAI: inspection.inspection_cai,
            OPPIS: inspection.OPPISLineIndicator,
            EWORequired: inspection.no_ewo_required == '0' ? 'No' : (inspection.no_ewo_required ? 'Yes' : ''),
            serviceClass: this.serviceCodeLabel(inspection.ServiceClass),
            comments: inspection.inspection_comments || inspection.engineering_comments
          };
        }

        this.operatorService.getRepumpCounter(this.tagNumber)
          .subscribe((data: any) => {
            if (Array.isArray(data) && data.length === 0) {
              this.totalRepumpCount = 0;
            } else if (typeof data === 'number') {
              this.totalRepumpCount = data;
            } else if (data && typeof data.repumpCounter === 'number') {
              this.totalRepumpCount = data.repumpCounter;
            } else {
              this.totalRepumpCount = 0;
            }
          });

        // Map OA data
        if (response.OA && response.OA.length > 0) {
          const oa = response.OA[0];
          this.oaData = {
            permanentRepairDate: this.formatDate(oa.permanent_repair_date),
            namoDate: this.formatDate(oa.NAMODate),
            engineerCAI: oa.engineering_cai,
            comments: oa.engineering_comments
          };
        }
        // Map Repump History data
        if (response.Repump_History && response.Repump_History.length > 0) {
          this.repumpHistoryData = response.Repump_History.map((record: any) => ({
            repumpId: record.repump_id || record.Repump_ID,
            tagNumber: record.tag_number || record.Tag_Number,
            repumpDate: this.formatDateOnly(record.repump_date || record.Repump_Date),
            repumpCAI: record.repump_cai || record.CAI,
            repumpComments: record.repump_comments || record.Repump_Comments || ''
          }));
        } else if (response.Repump_History) {
          this.repumpHistoryData = [];
        }
      },
      error: (error) => {
        console.error('Error in getHomeData:', error);
      }
    });      // Call getHomeApprovalData
    this.homeApprovalService.getHomeApprovalData(this.tagNumber, '', true).subscribe({
      next: (response) => {
        if (response.approvalData) {
          this.approvalRecords = response.approvalData.map((record: any) => ({
            id: record.ID,
            position: record.Position,
            requirements: record.Requirements,
            positionCAI: record.PositionCAI,
            name: record.PositionName,
            email: record.PositionEmail,
            emailSentDate: this.formatDate(record.EmailSentDate),
            approvedBy: record.ApprovedBy,
            approvedDate: this.formatDate(record.ApprovedDate),
            approveStatus: record.ApproveStatus,
            submitBy: record.SubmitBy,
            btnPending: record.btnPending,
            btnDelete: record.btnDelete,
            btnApprove: record.btnApprove
          }));

        }

        // Set button visibility based on response flags
        this.showSaveButton = response.button_Save_IsVisible || false;
        this.showReOpenButton = response.button_ReOpen_IsVisible || false;
        this.showReSendButton = response.button_ReSend_IsVisible || false;
      }, error: (error) => {
        console.error('Error in getHomeApprovalData:', error);
      }
    });    // Call getHomeRePumpApprovalData
    this.homeApprovalService.getHomeRePumpApprovalData(this.tagNumber, '', true).subscribe({
      next: (response) => {

        // Map repump history data from rePumpData (this takes priority)
        if (response.rePumpData && response.rePumpData.length > 0) {
          this.repumpHistoryData = response.rePumpData.map((record: any) => ({
            repumpId: record.repump_id || record.Repump_ID,
            tagNumber: record.tag_number || record.Tag_Number,
            repumpDate: this.formatDateOnly(record.repump_date || record.Repump_Date),
            repumpCAI: record.repump_cai || record.CAI,
            repumpComments: record.repump_comments || record.Repump_Comments || ''
          }));
        } else if (!this.repumpHistoryData || this.repumpHistoryData.length === 0) {
          // Only clear if we don't already have data from getHomeData
          this.repumpHistoryData = [];
        }

        // Map repump approval records
        if (response.repumpApprovalData) {
          this.repumpApprovalRecords = response.repumpApprovalData.map((record: any) => ({
            id: record.ID,
            tagNumber: record.TagNumber,
            positionPump: record.PositionPump,
            requirements: record.Requirements,
            positionPumpCAI: record.PositionPumpCAI,
            positionPumpName: record.PositionPumpName,
            positionPumpEmail: record.PositionPumpEmail,
            emailPumpSentDate: this.formatDate(record.EmailPumpSentDate),
            approvedPumpBy: record.ApprovedPumpBy,
            approvedPumpDate: this.formatDate(record.ApprovedPumpDate),
            approvePumpStatus: record.ApprovePumpStatus,
            submitBy: record.SubmitBy,
            btnRepumpApprove: record.btnRepumpApprove,
            btnRepumpDelete: record.btnRepumpDelete,
            btnRepumpPending: record.btnRepumpPending
          }));
        }
        // Set repump button visibility based on response flags
        this.showRepumpSaveButton = response.button_RePumpSave_IsVisible || false;
        this.showRepumpReOpenButton = response.button_RePumpReOpen_IsVisible || false;
        this.showRepumpReSendButton = response.button_RePumpReSend_IsVisible || false;
      },
      error: (error) => {
        console.error('Error in getHomeRePumpApprovalData:', error);
      }
    });
  }
  getTlrStatusText(status: string): string {
    switch (status) {
      case 'I':
        return 'Installed';
      case 'R':
        return 'Removed';
      case 'P':
        return 'Pending';
      default:
        return status;
    }
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  }  /**
   * Format date for display (date only, no time)
   */
  formatDateOnly(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  }

  // Helper methods for button visibility logic
  shouldShowApproveButton(record: any): boolean {
    const result = record.approveStatus === 'Pending' &&
      record.positionCAI?.toLowerCase() === this.loggedInCAI?.toLowerCase()
    return result;
  }

  shouldShowPendingButton(record: any): boolean {
    const result = record.approveStatus === 'Pending' &&
      record.positionCAI?.toLowerCase() !== this.loggedInCAI?.toLowerCase()
    return result;
  }

  shouldShowApprovedButton(record: any): boolean {
    const result = record.approveStatus === 'Approved'
    return result;
  }

  shouldShowDeleteButton(record: any): boolean {
    const result = (record.approveStatus === '' && record.btnDelete === 'True') || (record.approveStatus === 'Pending' && record.btnDelete === 'True')
    return result;
  }
  // Helper methods for repump button visibility logic
  shouldShowRepumpApproveButton(record: any): boolean {
    const result = record.approvePumpStatus === 'Pending' &&
      record.positionPumpCAI?.toLowerCase() === this.loggedInCAI?.toLowerCase();
    return result;
  }

  shouldShowRepumpPendingButton(record: any): boolean {
    const result = record.approvePumpStatus === 'Pending' &&
      record.positionPumpCAI?.toLowerCase() !== this.loggedInCAI?.toLowerCase();
    return result;
  }

  shouldShowRepumpApprovedButton(record: any): boolean {
    const result = record.approvePumpStatus === 'Approved';
    return result;
  }

  shouldShowRepumpDeleteButton(record: any): boolean {
    const result = (record.approvePumpStatus === '' && record.btnRepumpDelete === 'True') ||
      (record.approvePumpStatus === 'Pending' && record.btnRepumpDelete === 'True');
    return result;
  }

  /**
   * Handle Approve button click
   */
  approveRecord(record: any): void {
    // Call the service method with record ID, tag number, and logged in CAI
    this.homeApprovalService.approveRowDataInApproval(record, this.tagNumber, this.loggedInCAI).subscribe({
      next: (response) => {
        // Check if the response indicates success
        if (response.success !== false) {
          this.messageService.showSuccess('Record approved successfully');
          this.responseMessage = response.message || '';
          // Refresh the approval data to update the UI
          this.refreshApprovalData();
        } else {
          this.messageService.showError(response.error || 'Failed to approve record'
          );
        }
      },
      error: (error) => {
        console.error('Error approving record:', error);
        this.messageService.showError('Failed to approve record. Please try again.'
        );
      }
    });
  }  /**
   * Handle Save Changes button click
   */
  saveApprovalChanges(): void {
    // Reset validation error display flag and other messages
    this.showValidationErrors = false;
    this.recordUpdated = false;
    this.noRecordsTOSave = false;
    this.emailSent = false; // Set flag to indicate emails were re-sent
    // Get current facility ID
    const facilityId = this.homeApprovalService.getCurrentFacilityId();

    // Get records that are editable (id === '0') and have CAI entered by user
    const editableRecordsWithNewCai = this.approvalRecords.filter(record =>
      record.id === '0' && record.positionCAI && record.positionCAI.trim() !== ''
    );
    // If no new CAIs entered in editable fields, show "No Record(s) to 'SAVE'" message
    if (editableRecordsWithNewCai.length === 0) {
      this.noRecordsTOSave = true;
      return; // Exit early, no network call
    }

    // Validate all user IDs before saving
    this.validateUserIdsBeforeSave(editableRecordsWithNewCai, facilityId);
  }  /**
   * Validate all user IDs before performing save operation
   */
  private validateUserIdsBeforeSave(recordsToValidate: any[], facilityId: string): void {
    // Only validate records that actually have positionCAI entered
    const recordsWithCAI = recordsToValidate.filter(record =>
      record.positionCAI && record.positionCAI.trim() !== ''
    );

    if (recordsWithCAI.length === 0) {
      // No records with CAI to validate, proceed with save
      this.performSaveOperation(facilityId);
      return;
    }

    const validationObservables = recordsWithCAI.map(record =>
      this.adminService.getUserInfoByUserID(record.positionCAI)
    );    // Use forkJoin to execute all validation calls simultaneously
    forkJoin(validationObservables).subscribe({
      next: (results) => {
        let hasInvalidUsers = false;

        results.forEach((result, index) => {
          // Check if result is null/undefined or if it indicates an invalid user
          // Adjust this condition based on your API response structure
          if (!result ||
            (typeof result === 'object' && (!result.name && !result.fullName && !result.displayName))) {
            hasInvalidUsers = true;
          } else {
          }
        });

        if (hasInvalidUsers) {
          this.showValidationErrors = true;
        } else {
          // All user IDs are valid, proceed with save operation
          this.performSaveOperation(facilityId);
        }
      },
      error: (error) => {
        this.showValidationErrors = true;
      }
    });
  }

  /**
   * Perform the actual save operation after validations pass
   */
  private performSaveOperation(facilityId: string): void {
    // Build the payload according to the required structure
    const payload = {
      facilityId: facilityId,
      tagNumber: this.tagNumber,
      email: this.loggedInCAI,
      approvalGridRow: this.approvalRecords.map(record => ({
        position: record.position,
        email: record.positionCAI,
        lblCAIError: record.positionCAI && record.id === '0' ? 'Invalid User ID' : ''
      }))
    };
    // Call the service method
    this.homeApprovalService.saveChangesOnPostApproval(payload).subscribe({
      next: (response) => {
        this.recordUpdated = true; // Set flag to indicate record was updated
        this.messageService.showSuccess('Changes saved successfully'
        );
        this.responseMessage = response.message || '';

        // Refresh the approval data
        this.refreshApprovalData();
      },
      error: (error) => {
        console.error('Error saving changes:', error);
        this.messageService.showError('Failed to save changes. Please try again.');
      }
    });
  }
  /**
   * Refresh approval data after saving changes
   */
  private refreshApprovalData(): void {
    // Reset validation error display flag on refresh
    this.showValidationErrors = false;

    this.homeApprovalService.getHomeApprovalData(this.tagNumber, '', true).subscribe({
      next: (response) => {
        // Update approval records
        if (response.approvalData) {
          this.approvalRecords = response.approvalData.map((record: any) => ({
            id: record.ID,
            position: record.Position,
            requirements: record.Requirements,
            positionCAI: record.PositionCAI,
            name: record.PositionName,
            email: record.PositionEmail,
            emailSentDate: this.formatDate(record.EmailSentDate),
            approvedBy: record.ApprovedBy,
            approvedDate: this.formatDate(record.ApprovedDate),
            approveStatus: record.ApproveStatus,
            submitBy: record.SubmitBy,
            btnPending: record.btnPending,
            btnDelete: record.btnDelete,
            btnApprove: record.btnApprove
          }));

        }

        // Update button visibility
        this.showSaveButton = response.button_Save_IsVisible || false;
        this.showReOpenButton = response.button_ReOpen_IsVisible || false;
        this.showReSendButton = response.button_ReSend_IsVisible || false;
      },
      error: (error) => {
        console.error('Error refreshing approval data:', error);
      }
    });
  }

  /**
   * Handle Delete button click
   */
  deleteRecord(recordId: any): void {
    // Call the service method with record ID and tag number
    this.homeApprovalService.deleteApprovalRecordId(recordId, this.tagNumber).subscribe({
      next: (response) => {
        this.messageService.showSuccess('Record deleted successfully');
        this.responseMessage = '';
        // Refresh the approval data to update the UI
        this.refreshApprovalData();
      },
      error: (error) => {
        console.error('Error deleting record:', error);
        this.messageService.showError('Failed to delete record. Please try again.');
      }
    });
  }

  goToDetails() {
    this.router.navigate(['/details-section', this.tagNumber]);
  }

  /**
   * Handle Re-Send Approval Email(s) button click
   */
  reSendApprovalEmails(): void {
    this.emailSent = false; // Set flag to indicate emails were re-sent
    this.recordUpdated = false;
    // Call the service method with tag number
    this.homeApprovalService.reSendApprovalEmail(this.tagNumber).subscribe({
      next: (response) => {
        // Check if the response indicates success
        if (response.success !== false) {
          this.emailSent = true; // Set flag to indicate emails were re-sent
          this.messageService.showSuccess('Approval emails sent successfully'
          );
        } else {
          this.messageService.showError(response.error || 'Failed to send approval emails'
          );
        }
      },
      error: (error) => {
        console.error('Error sending approval emails:', error); this.messageService.showError('Failed to send approval emails. Please try again.'
        );
      }
    });
  }
  /**
   * Handle Re-Open Approvals button click
   */
  reOpenApprovals(): void {
    // Call the service method with tag number
    this.homeApprovalService.reOpenApproval(this.tagNumber).subscribe({
      next: (response) => {
        // Check if the response indicates success
        if (response && response.message && response.approvalData) {
          this.messageService.showSuccess(response.message || 'Approvals reopened successfully');
          // Update approval records directly from response data
          this.approvalRecords = response.approvalData.map((record: any) => ({
            id: record.ID,
            position: record.Position,
            requirements: record.Requirements,
            positionCAI: record.PositionCAI,
            name: record.PositionName,
            email: record.PositionEmail,
            emailSentDate: this.formatDate(record.EmailSentDate),
            approvedBy: record.ApprovedBy,
            approvedDate: this.formatDate(record.ApprovedDate),
            approveStatus: record.ApproveStatus,
            submitBy: record.SubmitBy,
            btnPending: record.btnPending,
            btnDelete: record.btnDelete,
            btnApprove: record.btnApprove
          }));



          // Update button visibility from response
          this.showSaveButton = response.button_Save_IsVisible || false;
          this.showReOpenButton = response.button_ReOpen_IsVisible || false;
          this.showReSendButton = response.button_ReSend_IsVisible || false;

          // Reset validation error display flag
          this.showValidationErrors = false;
        } else {
          this.messageService.showError(response?.error || 'Failed to reopen approvals');
        }
      },
      error: (error) => {
        console.error('Error reopening approvals:', error);
        this.messageService.showError('An error occurred while reopening approvals. Please try again.');
      }
    });
  }

  rePumpReOpenApprovals(): void {
    // Call the service method with tag number
    this.homeApprovalService.rePumpReopenOpenApproval(this.tagNumber).subscribe({
      next: (response) => {
        // Check if the response indicates success
        if (response && response.success !== false) {
          this.messageService.showSuccess(response.message || 'Repump approvals reopened successfully');

          // Update repump approval records directly from response
          if (response.repumpApprovalData) {
            this.repumpApprovalRecords = response.repumpApprovalData.map((record: any) => ({
              id: record.ID,
              tagNumber: record.TagNumber,
              positionPump: record.PositionPump,
              requirements: record.Requirements,
              positionPumpCAI: record.PositionPumpCAI,
              positionPumpName: record.PositionPumpName,
              positionPumpEmail: record.PositionPumpEmail,
              emailPumpSentDate: this.formatDate(record.EmailPumpSentDate),
              approvedPumpBy: record.ApprovedPumpBy,
              approvedPumpDate: this.formatDate(record.ApprovedPumpDate),
              approvePumpStatus: record.ApprovePumpStatus,
              submitBy: record.SubmitBy,
              btnRepumpApprove: record.btnRepumpApprove,
              btnRepumpDelete: record.btnRepumpDelete,
              btnRepumpPending: record.btnRepumpPending
            }));
          }

          // Update button visibility based on response
          this.showRepumpSaveButton = response.button_PumpSave_IsVisible || false;
          this.showRepumpReOpenButton = response.button_PumpReOpen_IsVisible || false;
        } else {
          this.messageService.showError(response?.error || 'Failed to reopen repump approvals'
          );
        }
      },
      error: (error) => {
        console.error('Error reopening repump approvals:', error);
        this.messageService.showError('An error occurred while reopening repump approvals'
        );
      }
    });
  }

  /**
   * Handle Repump Approve button click
   */
  approveRepumpRecord(record: any): void {
    // Call the correct repump approval service method
    this.homeApprovalService.approveRowDataInRepumpApproval(record.id, this.tagNumber, this.loggedInCAI).subscribe({
      next: (response) => {
        this.repumpMessage = response.message;
        if (response.success !== false) {
          this.messageService.showSuccess('Repump record approved successfully');
          this.responseMessage = response.message;
          this.refreshRepumpApprovalData();
        } else {
          this.messageService.showError(response.error || 'Failed to approve repump record'
          );
        }
      },
      error: (error) => {
        console.error('Error approving repump record:', error);
        this.messageService.showError('Failed to approve repump record. Please try again.'
        );
      }
    });
  }
  /**
   * Handle Repump Delete button click
   */
  deleteRepumpRecord(record: any): void {
    this.homeApprovalService.deleteRePumpApprovalRecordId(record.id, this.tagNumber).subscribe({
      next: (response) => {
        if (response.success !== false) {
          this.messageService.showSuccess('Repump record deleted successfully');
          this.repumpMessage = '';

          this.refreshRepumpApprovalData();
        } else {
          this.messageService.showError('Failed to delete repump record');
        }
      },
      error: (error) => {
        console.error('Error deleting repump record:', error);
        this.messageService.showError(
          'Failed to delete repump record. Please try again.'
        );
      }
    });
  }  /**
   * Handle Repump Save Changes button click
   */
  saveRepumpApprovalChanges(): void {
    // Reset validation error display flag and other messages
    this.showRepumpValidationErrors = false;

    const facilityId = this.homeApprovalService.getCurrentFacilityId();

    // Get records that are editable (id === '0') and have CAI entered by user
    const editableRecordsWithNewCai = this.repumpApprovalRecords.filter(record =>
      record.id === '0' && record.positionPumpCAI && record.positionPumpCAI.trim() !== ''
    );
    // If no new CAIs entered in editable fields, show error message
    if (editableRecordsWithNewCai.length === 0) {
      this.messageService.showError('Please enter at least one User ID'
      );
      return;
    }

    // Validate all user IDs before saving
    this.validateRepumpUserIdsBeforeSave(editableRecordsWithNewCai, facilityId);
  }

  /**
   * Validate all repump user IDs before performing save operation
   */
  private validateRepumpUserIdsBeforeSave(recordsToValidate: any[], facilityId: string): void {
    // Only validate records that actually have positionPumpCAI entered
    const recordsWithCAI = recordsToValidate.filter(record =>
      record.positionPumpCAI && record.positionPumpCAI.trim() !== ''
    );

    if (recordsWithCAI.length === 0) {
      // No records with CAI to validate, proceed with save
      this.performRepumpSaveOperation(facilityId);
      return;
    }

    const validationObservables = recordsWithCAI.map(record =>
      this.adminService.getUserInfoByUserID(record.positionPumpCAI)
    );

    // Use forkJoin to execute all validation calls simultaneously
    forkJoin(validationObservables).subscribe({
      next: (results) => {
        let hasInvalidUsers = false;

        results.forEach((result, index) => {
          // Check if result is null/undefined or if it indicates an invalid user
          // Adjust this condition based on your API response structure
          if (!result ||
            (typeof result === 'object' && (!result.name && !result.fullName && !result.displayName))) {
            hasInvalidUsers = true;
          }

        });

        if (hasInvalidUsers) {
          this.showRepumpValidationErrors = true;
        } else {
          // All user IDs are valid, proceed with save operation
          this.performRepumpSaveOperation(facilityId);
        }
      },
      error: (error) => {
        console.error('Error validating repump user IDs:', error);
        this.showRepumpValidationErrors = true;
      }
    });
  }

  /**
   * Perform the actual repump save operation after validations pass
   */
  private performRepumpSaveOperation(facilityId: string): void {
    const payload = {
      facilityId: facilityId,
      tagNumber: this.tagNumber,
      email: this.loggedInCAI,
      approvalGridRow: this.repumpApprovalRecords.map(record => ({
        position: record.positionPump,
        email: record.positionPumpCAI,
        lblCAIError: record.positionPumpCAI && record.id === '0' ? 'Invalid User ID' : ''
      }))
    };


    this.homeApprovalService.postRempumpApprovaSave(payload).subscribe({
      next: (response) => {
        if (response.success !== false) {
          this.messageService.showSuccess('Repump changes saved successfully'
          );
          this.repumpMessage = response.message || '';
          this.refreshRepumpApprovalData();
        } else {
          this.messageService.showError(response.error || 'Failed to save repump changes'
          );
        }
      },
      error: (error) => {
        console.error('Error saving repump changes:', error);
        this.messageService.showError('Failed to save repump changes. Please try again.'
        );
      }
    });
  }

  /**
   * Refresh repump approval data after operations
   */
  private refreshRepumpApprovalData(): void {
    this.homeApprovalService.getHomeRePumpApprovalData(this.tagNumber, '', true).subscribe({
      next: (response) => {
        // Map repump history data from rePumpData
        if (response.rePumpData && response.rePumpData.length > 0) {
          this.repumpHistoryData = response.rePumpData.map((record: any) => ({
            repumpId: record.repump_id || record.Repump_ID,
            tagNumber: record.tag_number || record.Tag_Number,
            repumpDate: this.formatDate(record.repump_date || record.Repump_Date),
            repumpCAI: record.repump_cai || record.CAI,
            repumpComments: record.repump_comments || record.Repump_Comments || ''
          }));
        } else {
          this.repumpHistoryData = [];
        }

        if (response.repumpApprovalData) {
          this.repumpApprovalRecords = response.repumpApprovalData.map((record: any) => ({
            id: record.ID,
            tagNumber: record.TagNumber,
            positionPump: record.PositionPump,
            requirements: record.Requirements,
            positionPumpCAI: record.PositionPumpCAI,
            positionPumpName: record.PositionPumpName,
            positionPumpEmail: record.PositionPumpEmail,
            emailPumpSentDate: this.formatDate(record.EmailPumpSentDate),
            approvedPumpBy: record.ApprovedPumpBy,
            approvedPumpDate: this.formatDate(record.ApprovedPumpDate),
            approvePumpStatus: record.ApprovePumpStatus,
            submitBy: record.SubmitBy,
            btnRepumpApprove: record.btnRepumpApprove,
            btnRepumpDelete: record.btnRepumpDelete,
            btnRepumpPending: record.btnRepumpPending
          }));
        }
        // Update button visibility
        this.showRepumpSaveButton = response.button_RePumpSave_IsVisible || false;
        this.showRepumpReOpenButton = response.button_RePumpReOpen_IsVisible || false;
        this.showRepumpReSendButton = response.button_RePumpReSend_IsVisible || false;
      },
      error: (error) => {
        console.error('Error refreshing repump approval data:', error);
      }
    });
  }

  /**
   * Handle Repump Re-Send Approval Emails button click
   */
  reSendRepumpApprovalEmails(): void {
    this.homeApprovalService.reSendRepumpApprovalEmail(this.tagNumber).subscribe({
      next: (response) => {
        if (response.success !== false) {
          this.messageService.showSuccess('Repump approval emails sent successfully'
          );
        } else {
          this.messageService.showError(response.error || 'Failed to send repump approval emails'
          );
        }
      },
      error: (error) => {
        console.error('Error sending repump approval emails:', error);
        this.messageService.showError('Failed to send repump approval emails. Please try again.'
        );
      }
    });
  }
  serviceCodeLabel(input: any) {
    let lbl;

    switch (input) {
      case "1":
        lbl = "Class 1: Services with the highest potential of resulting in an immediate emergency if a leak were to occur";
        break;
      case "2":
        lbl = "Class 2: Services not included in other classes";
        break;
      case "3":
        lbl = "Class 3: Services either flammable but do not significantly vaporize when they leak(below the flash point) or flammable but are in remote areas and operate below the boiling point";
        break;
      case "4":
        lbl = "Class 4: Services that are nonflammable and nontoxic are in Class 4, as are most utility services";
        break;
      default:
        lbl = "";
    }

    return lbl;
  }

  /**
   * Format requirements text to display each bullet point on a separate line
   */
  formatRequirements(requirements: string): string[] {
    if (!requirements) return [];

    // Split by bullet points (•) and filter out empty strings
    const formattedRequirements = requirements
      .split('•')
      .map(req => req.trim())
      .filter(req => req.length > 0);

    return formattedRequirements;
  }
}